import {
  LayoutDashboard,
  Users,
  UsersRound,
  QrCode,
  Trophy,
  Award,
  BookOpen,
  BarChart3,
  Bell,
  ShieldCheck,
  Settings,
  FileText,
  Scale,
  FolderKanban,
  Mic2,
  CalendarDays,
  Route,
  CalendarRange,
  Landmark,
  Globe2,
  UserCheck,
  Medal,
  MessageSquare,
  MessagesSquare,
  UserCog,
  Gavel,
  Vote,
  ClipboardCheck,
  type LucideIcon,
} from 'lucide-react';
import { isValidEventWorkspaceId } from '@/lib/resolve-static-param';

export type ActivityNavItem = {
  key: string;
  slug: string;
  label: string;
  icon: LucideIcon;
  exact?: boolean;
  description?: string;
  group?: string;
};

export type ActivityNavGroup = {
  key: string;
  label: string;
  items: ActivityNavItem[];
};

export type ActivityModuleCatalogEntry = {
  key: string;
  label: string;
  group: string;
  slug: string;
  description?: string | null;
  source?: string;
};

/** Canonical module metadata — sidebar/overview resolve icons from this map. */
export const ACTIVITY_MODULE_META: Record<string, Omit<ActivityNavItem, 'key'> & { group: string }> = {
  overview: {
    slug: 'overview',
    label: 'Overview & Analytics',
    icon: LayoutDashboard,
    exact: true,
    group: 'dashboard',
    description: 'Activity dashboard and analytics',
  },
  analytics: {
    slug: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    group: 'dashboard',
    description: 'Engagement and performance metrics',
  },
  participants: {
    slug: 'participants',
    label: 'Participants',
    icon: Users,
    group: 'people',
    description: 'Registrations and participant roster',
  },
  teams: {
    slug: 'teams',
    label: 'Teams',
    icon: UsersRound,
    group: 'people',
    description: 'Team formation and assignments',
  },
  delegates: {
    slug: 'delegates',
    label: 'Delegates',
    icon: UserCheck,
    group: 'people',
    description: 'Delegate roster and allocations',
  },
  attendance: {
    slug: 'attendance',
    label: 'Attendance',
    icon: QrCode,
    group: 'people',
    description: 'Check-in and session scans',
  },
  projects: {
    slug: 'projects',
    label: 'Projects',
    icon: FolderKanban,
    group: 'program',
    description: 'Team projects and submissions',
  },
  tasks: {
    slug: 'tasks',
    label: 'Tasks & Assignments',
    icon: FolderKanban,
    group: 'program',
    description: 'Workshop milestone assignments and submissions review',
  },
  results: {
    slug: 'results',
    label: 'Results',
    icon: Trophy,
    group: 'program',
    description: 'Scores, rankings, and outcomes',
  },
  leaderboard: {
    slug: 'leaderboard',
    label: 'Leaderboard',
    icon: Medal,
    group: 'program',
    description: 'Live rankings and scores',
  },
  certificates: {
    slug: 'certificates',
    label: 'Certificates',
    icon: Award,
    group: 'program',
    description: 'Issue and manage certificates',
  },
  reports: {
    slug: 'reports',
    label: 'Reports',
    icon: BookOpen,
    group: 'program',
    description: 'Exports and activity reports',
  },
  speakers: {
    slug: 'speakers',
    label: 'Speakers',
    icon: Mic2,
    group: 'program',
    description: 'Keynotes, panels, and speaker roster',
  },
  speaker: {
    slug: 'speaker',
    label: 'Speaker',
    icon: Mic2,
    group: 'program',
    description: 'Workshop speaker and host',
  },
  sessions: {
    slug: 'sessions',
    label: 'Sessions',
    icon: CalendarDays,
    group: 'program',
    description: 'Talks, panels, and breakouts',
  },
  tracks: {
    slug: 'tracks',
    label: 'Tracks',
    icon: Route,
    group: 'program',
    description: 'Thematic program tracks',
  },
  agenda: {
    slug: 'agenda',
    label: 'Agenda',
    icon: CalendarRange,
    group: 'program',
    description: 'Published schedule across tracks',
  },
  committees: {
    slug: 'committees',
    label: 'Committees',
    icon: Landmark,
    group: 'program',
    description: 'Committee setup and staffing',
  },
  countries: {
    slug: 'countries',
    label: 'Countries',
    icon: Globe2,
    group: 'program',
    description: 'Country assignments and pool',
  },
  awards: {
    slug: 'awards',
    label: 'Awards',
    icon: Medal,
    group: 'program',
    description: 'Awards and recognition',
  },
  evaluation: {
    slug: 'evaluation',
    label: 'Evaluation',
    icon: Scale,
    group: 'judging',
    description: 'Judges, rubrics, and evaluation flow',
  },
  communications: {
    slug: 'communications',
    label: 'Communications',
    icon: Bell,
    group: 'comms',
    description: 'Announcements and updates',
  },
  chat: {
    slug: 'chat',
    label: 'Activity Chat',
    icon: MessageSquare,
    group: 'comms',
    description: 'Private chat for this activity',
  },
  'activity-team': {
    slug: 'activity-team',
    label: 'Assign Team',
    icon: UserCog,
    group: 'admin',
    description: 'Staff assigned to this activity',
  },
  'team-chat': {
    slug: 'team-chat',
    label: 'Team Chat',
    icon: MessagesSquare,
    group: 'comms',
    description: 'Private team chat',
  },
  permissions: {
    slug: 'permissions',
    label: 'Permissions',
    icon: ShieldCheck,
    group: 'admin',
    description: 'Roles and workspace access',
  },
  settings: {
    slug: 'settings',
    label: 'Settings',
    icon: Settings,
    group: 'admin',
    description: 'Activity configuration',
  },
  resources: {
    slug: 'resources',
    label: 'Resources',
    icon: FileText,
    group: 'admin',
    description: 'Files, links, and materials',
  },
  'parliamentary-sessions': {
    slug: 'parliamentary-sessions',
    label: 'Parliamentary Sessions',
    icon: Landmark,
    group: 'sessions',
    description: 'Formal & informal debate, caucus tracking',
  },
  'legislative-sessions': {
    slug: 'legislative-sessions',
    label: 'Legislative Sessions',
    icon: Scale,
    group: 'sessions',
    description: 'Bill drafting, roll call, and voting',
  },
  'position-papers': {
    slug: 'position-papers',
    label: 'Position Papers',
    icon: FileText,
    group: 'diplomacy',
    description: 'Delegate paper submission & review',
  },
  resolutions: {
    slug: 'resolutions',
    label: 'Draft Resolutions',
    icon: FileText,
    group: 'sessions',
    description: 'Draft resolutions, sponsors, amendments',
  },
  mentorship: {
    slug: 'mentorship',
    label: 'Mentorship',
    icon: UserCheck,
    group: 'people',
    description: 'Mentors, assignments, and office hours',
  },
  'check-ins': {
    slug: 'check-ins',
    label: 'Check-ins',
    icon: ClipboardCheck,
    group: 'people',
    description: 'Attendance and check-in records',
  },
  jury: {
    slug: 'jury',
    label: 'Jury',
    icon: Gavel,
    group: 'judging',
    description: 'Jury invitations and assignments',
  },
  sponsors: {
    slug: 'sponsors',
    label: 'Sponsors',
    icon: Trophy,
    group: 'networking',
    description: 'Sponsor tiers, assets, and branding',
  },
  rounds: {
    slug: 'rounds',
    label: 'Rounds & Timer',
    icon: CalendarRange,
    group: 'competition',
    description: 'Competition rounds and timer controls',
  },
  motions: {
    slug: 'motions',
    label: 'Motions & Topics',
    icon: FileText,
    group: 'competition',
    description: 'Debate motions, formats, and info slides',
  },
  'mun-voting': {
    slug: 'mun-voting',
    label: 'Voting & Division',
    icon: Vote,
    group: 'sessions',
    description: 'Live roll call voting, countdown timer, and certified results',
  },
  voting: {
    slug: 'voting',
    label: 'Assembly Voting',
    icon: Vote,
    group: 'sessions',
    description: 'Live roll call voting, division, and certified results',
  },
  pairings: {
    slug: 'pairings',
    label: 'Team Pairings',
    icon: UsersRound,
    group: 'competition',
    description: 'Matchups, room assignments, and adjudicators',
  },
  problems: {
    slug: 'problems',
    label: 'Question / Problem Bank',
    icon: FolderKanban,
    group: 'content',
    description: 'Questions, testcases, and problem sets',
  },
  'paper-submissions': {
    slug: 'paper-submissions',
    label: 'Paper Submissions',
    icon: BookOpen,
    group: 'papers',
    description: 'Research paper abstracts and files',
  },
  plagiarism: {
    slug: 'plagiarism',
    label: 'Plagiarism & Review',
    icon: ShieldCheck,
    group: 'papers',
    description: 'Similarity checks and reviewer scorecards',
  },
};

const GROUP_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  people: 'People',
  program: 'Program',
  judging: 'Judging',
  comms: 'Communications',
  admin: 'Administration',
  diplomacy: 'Councils & Delegates',
  sessions: 'Debate & Assembly',
  teams: 'Teams & Participants',
  submissions: 'Submissions & Competition',
  competition: 'Competition & Rounds',
  content: 'Questions & Rounds',
  contest: 'Problems & Testing',
  papers: 'Papers & Review',
  schedule: 'Presentation Schedule',
  curriculum: 'Curriculum & Labs',
  students: 'Students & Progress',
  attendees: 'Attendees',
  networking: 'Attendees & Partners',
  adjudication: 'Adjudication',
  pitching: 'Pitching & Judging',
};

const GROUP_ORDER = [
  'dashboard',
  'diplomacy',
  'sessions',
  'teams',
  'submissions',
  'competition',
  'content',
  'contest',
  'papers',
  'schedule',
  'curriculum',
  'students',
  'attendees',
  'networking',
  'adjudication',
  'pitching',
  'people',
  'program',
  'judging',
  'comms',
  'admin',
];

/** @deprecated Prefer resolving from dashboard module_catalog + ACTIVITY_MODULE_META */
export const ACTIVITY_NAV_ITEMS: ActivityNavItem[] = Object.entries(ACTIVITY_MODULE_META).map(
  ([key, meta]) => ({ key, ...meta }),
);

export const ACTIVITY_NAV_GROUPS: ActivityNavGroup[] = GROUP_ORDER.map((key) => ({
  key,
  label: GROUP_LABELS[key] ?? key,
  items: ACTIVITY_NAV_ITEMS.filter((item) => item.group === key),
}));

export function resolveActivityNavItem(
  key: string,
  catalogEntry?: ActivityModuleCatalogEntry | null,
): ActivityNavItem | null {
  const meta = ACTIVITY_MODULE_META[key];
  if (!meta && !catalogEntry) return null;

  return {
    key,
    slug: catalogEntry?.slug ?? meta?.slug ?? key,
    label: catalogEntry?.label ?? meta?.label ?? key,
    icon: meta?.icon ?? FileText,
    exact: meta?.exact,
    description: catalogEntry?.description ?? meta?.description,
    group: catalogEntry?.group ?? meta?.group ?? 'program',
  };
}

export function activityNavItemsForModules(
  modules: string[],
  catalog?: ActivityModuleCatalogEntry[],
): ActivityNavItem[] {
  // Empty modules = still loading or unavailable — never dump the full catalog (dead links).
  if (!modules.length) return [];

  const catalogByKey = new Map((catalog ?? []).map((entry) => [entry.key, entry]));

  return modules
    .map((key) => resolveActivityNavItem(key, catalogByKey.get(key)))
    .filter((item): item is ActivityNavItem => item !== null);
}

export type ActivityTypeNavModuleItem = {
  key: string;
  label?: string;
  slug?: string;
  description?: string;
  exact?: boolean;
};

export type ActivityTypeNavGroupConfig = {
  key: string;
  label: string;
  modules: ActivityTypeNavModuleItem[];
};

export type ActivityTypeNavConfig = {
  groups: ActivityTypeNavGroupConfig[];
};

export const ACTIVITY_TYPE_MODULES_CONFIG: Record<string, ActivityTypeNavConfig> = {
  // 1. MUN (Model United Nations)
  mun: {
    groups: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        modules: [{ key: 'overview', label: 'Overview' }],
      },
      {
        key: 'diplomacy',
        label: 'Councils & Delegates',
        modules: [
          { key: 'committees', label: 'Councils', slug: 'committees' },
          { key: 'delegates', label: 'Delegates & Allocations', slug: 'delegates' },
          { key: 'position-papers', label: 'Position Papers', slug: 'position-papers' },
          { key: 'speakers-queue', label: 'Speakers Queue', slug: 'speakers-queue' },
          // { key: 'documents', label: 'Documents', slug: 'documents' },
          { key: 'alliances', label: 'Alliances', slug: 'alliances' },
          { key: 'secretariat', label: 'Secretariat', slug: 'secretariat' },
        ],
      },
      {
        key: 'resolutions',
        label: 'Resolutions',
        modules: [
          { key: 'resolutions', label: 'Draft Resolutions', slug: 'resolutions' },
          { key: 'motions', label: 'Motions & Caucuses', slug: 'mun-voting?tab=motions' },
          { key: 'mun-voting', label: 'Council Voting', slug: 'mun-voting' },
        ],
      },
      {
        key: 'judging',
        label: 'Awards & Attendance',
        modules: [
          { key: 'awards', label: 'Best Delegate & Awards', slug: 'awards' },
          { key: 'attendance', label: 'Session Attendance', slug: 'attendance' },
          { key: 'certificates', label: 'Certificates', slug: 'certificates' },
          { key: 'evaluation', label: 'Delegate Evaluation', slug: 'evaluation' },
        ],
      },
      {
        key: 'comms',
        label: 'Communications',
        modules: [
          { key: 'chat', label: 'Council Chat', slug: 'chat' },
          { key: 'team-chat', label: 'Organizers Chat', slug: 'team-chat' },
        ],
      },
      {
        key: 'admin',
        label: 'Administration',
        modules: [
          { key: 'activity-team', label: 'Assign Team', slug: 'activity-team' },
          { key: 'settings', label: 'Settings', slug: 'settings' },
        ],
      },
    ],
  },

  // Parliamentary Debate / Assembly
  parliamentary: {
    groups: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        modules: [
          { key: 'overview', label: 'Overview' },
          { key: 'command-center', label: 'Command Center', slug: 'command-center' },
        ],
      },
      {
        key: 'chamber',
        label: 'House & Members',
        modules: [
          { key: 'committees', label: 'Chambers & Houses', slug: 'committees' },
          { key: 'delegates', label: 'Members of House (MPs)', slug: 'delegates' },
          { key: 'position-papers', label: 'Policy Statements', slug: 'position-papers' },
        ],
      },
      {
        key: 'legislative',
        label: 'Legislative & Debates',
        modules: [
          { key: 'parliamentary-sessions', label: 'Parliamentary Sessions', slug: 'parliamentary-sessions' },
          { key: 'resolutions', label: 'Bills & Acts', slug: 'resolutions' },
          { key: 'motions', label: 'Motions & Caucuses', slug: 'mun-voting?tab=motions' },
          { key: 'mun-voting', label: 'House Division & Voting', slug: 'mun-voting' },
        ],
      },
      {
        key: 'judging',
        label: 'Decisions & Awards',
        modules: [
          { key: 'awards', label: 'Awards & Recognitions', slug: 'awards' },
          { key: 'attendance', label: 'Chamber Attendance', slug: 'attendance' },
          { key: 'certificates', label: 'Certificates', slug: 'certificates' },
        ],
      },
      {
        key: 'comms',
        label: 'Communications',
        modules: [
          { key: 'chat', label: 'Chamber Floor Chat', slug: 'chat' },
          { key: 'team-chat', label: 'Speaker & Staff Chat', slug: 'team-chat' },
        ],
      },
      {
        key: 'admin',
        label: 'Administration',
        modules: [
          { key: 'activity-team', label: 'Assign Marshals', slug: 'activity-team' },
          { key: 'settings', label: 'Settings', slug: 'settings' },
        ],
      },
    ],
  },

  state_legislative: {
    groups: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        modules: [
          { key: 'overview', label: 'Overview' },
        ],
      },
      {
        key: 'proceedings',
        label: 'Proceedings & Voting',
        modules: [
          { key: 'legislative-sessions', label: 'Legislative Sessions', slug: 'legislative-sessions' },
          { key: 'mun-voting', label: 'Assembly Division & Voting', slug: 'mun-voting' },
        ],
      },
      {
        key: 'house',
        label: 'Assembly',
        modules: [
          { key: 'delegates', label: 'Members', slug: 'delegates' },
          { key: 'attendance', label: 'Attendance', slug: 'attendance' },
          { key: 'awards', label: 'Awards', slug: 'awards' },
          { key: 'certificates', label: 'Certificates', slug: 'certificates' },
        ],
      },
      {
        key: 'admin',
        label: 'Administration',
        modules: [
          { key: 'activity-team', label: 'Assign Team', slug: 'activity-team' },
          { key: 'settings', label: 'Settings', slug: 'settings' },
        ],
      },
    ],
  },

  // 2. Hackathon
  hackathon: {
    groups: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        modules: [{ key: 'overview', label: 'Overview' }],
      },
      {
        key: 'people',
        label: 'People',
        modules: [
          { key: 'participants', label: 'Participants', slug: 'participants' },
          { key: 'teams', label: 'Teams', slug: 'teams' },
          { key: 'mentorship', label: 'Mentorship', slug: 'mentorship' },
          { key: 'check-ins', label: 'Check-ins', slug: 'check-ins' },
        ],
      },
      {
        key: 'submissions',
        label: 'Submissions',
        modules: [{ key: 'projects', label: 'Project Submissions', slug: 'projects' }],
      },
      {
        key: 'judging',
        label: 'Judging',
        modules: [
          { key: 'evaluation', label: 'Evaluation', slug: 'evaluation' },
          { key: 'jury', label: 'Jury', slug: 'jury' },
        ],
      },
      {
        key: 'program',
        label: 'Results & Awards',
        modules: [
          { key: 'leaderboard', label: 'Results / Leaderboard', slug: 'leaderboard' },
          { key: 'awards', label: 'Awards & Prizes', slug: 'awards' },
          { key: 'certificates', label: 'Certificates', slug: 'certificates' },
        ],
      },
      {
        key: 'comms',
        label: 'Communications',
        modules: [
          { key: 'communications', label: 'Communication', slug: 'communications' },
          { key: 'chat', label: 'Hacker Chat', slug: 'chat' },
        ],
      },
      {
        key: 'admin',
        label: 'Administration',
        modules: [
          { key: 'activity-team', label: 'Manage Team', slug: 'activity-team' },
          { key: 'settings', label: 'Settings', slug: 'settings' },
        ],
      },
    ],
  },

  // 3. Conference
  conference: {
    groups: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        modules: [{ key: 'overview', label: 'Overview' }],
      },
      {
        key: 'program',
        label: 'Program & Schedule',
        modules: [
          { key: 'sessions', label: 'Sessions', slug: 'sessions' },
          { key: 'speakers', label: 'Speakers', slug: 'speakers' },
          { key: 'tracks', label: 'Tracks', slug: 'tracks' },
          { key: 'agenda', label: 'Agenda', slug: 'agenda' },
        ],
      },
      {
        key: 'networking',
        label: 'Attendees & Partners',
        modules: [
          { key: 'participants', label: 'Attendee List', slug: 'participants' },
          { key: 'sponsors', label: 'Sponsors', slug: 'sponsors' },
          { key: 'attendance', label: 'Check-in & Badges', slug: 'attendance' },
          { key: 'certificates', label: 'Certificates', slug: 'certificates' },
        ],
      },
      {
        key: 'comms',
        label: 'Communications',
        modules: [
          { key: 'chat', label: 'Attendee Chat', slug: 'chat' },
          { key: 'team-chat', label: 'Organizer Chat', slug: 'team-chat' },
        ],
      },
      {
        key: 'admin',
        label: 'Administration',
        modules: [
          { key: 'activity-team', label: 'Assign Team', slug: 'activity-team' },
          { key: 'settings', label: 'Settings', slug: 'settings' },
        ],
      },
    ],
  },

  // 4. Debate Competition
  debate: {
    groups: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        modules: [{ key: 'overview', label: 'Overview' }],
      },
      {
        key: 'competition',
        label: 'Rounds & Motions',
        modules: [
          { key: 'rounds', label: 'Debate Rounds', slug: 'rounds' },
          { key: 'motions', label: 'Motions & Topics', slug: 'motions' },
          { key: 'pairings', label: 'Team Pairings', slug: 'pairings' },
          { key: 'leaderboard', label: 'Rankings & Standings', slug: 'leaderboard' },
        ],
      },
      {
        key: 'adjudication',
        label: 'Adjudication',
        modules: [
          { key: 'evaluation', label: 'Scores & Speaker Points', slug: 'evaluation' },
          { key: 'attendance', label: 'Debater Attendance', slug: 'attendance' },
          { key: 'certificates', label: 'Certificates', slug: 'certificates' },
        ],
      },
      {
        key: 'admin',
        label: 'Administration',
        modules: [
          { key: 'activity-team', label: 'Assign Team', slug: 'activity-team' },
          { key: 'settings', label: 'Settings', slug: 'settings' },
        ],
      },
    ],
  },

  // 5. Quiz Contest
  quiz: {
    groups: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        modules: [{ key: 'overview', label: 'Overview' }],
      },
      {
        key: 'content',
        label: 'Quiz Management',
        modules: [
          { key: 'activity-team', label: 'Manage Team', slug: 'activity-team' },
          { key: 'problems', label: 'Question Bank', slug: 'problems' },
          { key: 'rounds', label: 'Quiz Control', slug: 'rounds' },
          { key: 'settings', label: 'Quiz Settings', slug: 'settings' },
        ],
      },
      {
        key: 'people',
        label: 'Contestants & Scoring',
        modules: [
          { key: 'participants', label: 'Participants', slug: 'participants' },
          { key: 'leaderboard', label: 'Leaderboard', slug: 'leaderboard' },
          { key: 'results', label: 'Results', slug: 'results' },
          { key: 'certificates', label: 'Certificates', slug: 'certificates' },
        ],
      },
    ],
  },

  // 6. Coding Contest
  coding_contest: {
    groups: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        modules: [{ key: 'overview', label: 'Overview' }],
      },
      {
        key: 'contest',
        label: 'Problems & Testing',
        modules: [
          { key: 'problems', label: 'Problem Set', slug: 'problems' },
          { key: 'projects', label: 'Code Submissions', slug: 'projects' },
          { key: 'evaluation', label: 'Testcases & Rubrics', slug: 'evaluation' },
          { key: 'leaderboard', label: 'Live Leaderboard', slug: 'leaderboard' },
        ],
      },
      {
        key: 'people',
        label: 'Contestants',
        modules: [
          { key: 'participants', label: 'Contestants', slug: 'participants' },
          { key: 'attendance', label: 'Attendance', slug: 'attendance' },
          { key: 'certificates', label: 'Certificates', slug: 'certificates' },
        ],
      },
      {
        key: 'admin',
        label: 'Administration',
        modules: [
          { key: 'activity-team', label: 'Assign Team', slug: 'activity-team' },
          { key: 'settings', label: 'Settings', slug: 'settings' },
        ],
      },
    ],
  },

  // 7. Paper Presentation (Research Symposium)
  paper_presentation: {
    groups: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        modules: [{ key: 'overview', label: 'Overview' }],
      },
      {
        key: 'papers',
        label: 'Papers & Review',
        modules: [
          { key: 'paper-submissions', label: 'Paper Submissions', slug: 'paper-submissions' },
          { key: 'plagiarism', label: 'Plagiarism & Review', slug: 'plagiarism' },
          { key: 'evaluation', label: 'Reviewer Scores', slug: 'evaluation' },
        ],
      },
      {
        key: 'schedule',
        label: 'Presentation Schedule',
        modules: [
          { key: 'sessions', label: 'Presentation Sessions', slug: 'sessions' },
          { key: 'speakers', label: 'Authors / Presenters', slug: 'speakers' },
          { key: 'results', label: 'Rankings & Awards', slug: 'results' },
          { key: 'certificates', label: 'Certificates', slug: 'certificates' },
        ],
      },
      {
        key: 'admin',
        label: 'Administration',
        modules: [
          { key: 'activity-team', label: 'Assign Team', slug: 'activity-team' },
          { key: 'settings', label: 'Settings', slug: 'settings' },
        ],
      },
    ],
  },

  // 8. Workshop
  workshop: {
    groups: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        modules: [{ key: 'overview', label: 'Overview' }],
      },
      {
        key: 'program',
        label: 'Workshop Content',
        modules: [
          { key: 'sessions', label: 'Curriculum & Schedule', slug: 'sessions' },
          { key: 'tasks', label: 'Tasks & Assignments', slug: 'tasks' },
          { key: 'speaker', label: 'Instructors & Trainers', slug: 'speaker' },
          { key: 'resources', label: 'Resources & Downloads', slug: 'resources' },
        ],
      },
      {
        key: 'attendees',
        label: 'Attendees',
        modules: [
          { key: 'participants', label: 'Attendees', slug: 'participants' },
          { key: 'attendance', label: 'Check-in', slug: 'attendance' },
          { key: 'certificates', label: 'Certificates', slug: 'certificates' },
        ],
      },
      {
        key: 'comms',
        label: 'Communications',
        modules: [{ key: 'chat', label: 'Workshop Chat', slug: 'chat' }],
      },
      {
        key: 'admin',
        label: 'Administration',
        modules: [
          { key: 'activity-team', label: 'Assign Team', slug: 'activity-team' },
          { key: 'settings', label: 'Settings', slug: 'settings' },
        ],
      },
    ],
  },

  // 9. Bootcamp
  bootcamp: {
    groups: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        modules: [{ key: 'overview', label: 'Overview' }],
      },
      {
        key: 'curriculum',
        label: 'Curriculum & Labs',
        modules: [
          { key: 'sessions', label: 'Modules & Sessions', slug: 'sessions' },
          { key: 'resources', label: 'Lab Materials & Code', slug: 'resources' },
          { key: 'speakers', label: 'Instructors & Mentors', slug: 'speakers' },
        ],
      },
      {
        key: 'students',
        label: 'Students & Progress',
        modules: [
          { key: 'participants', label: 'Enrolled Students', slug: 'participants' },
          { key: 'attendance', label: 'Session Attendance', slug: 'attendance' },
          { key: 'certificates', label: 'Certificates', slug: 'certificates' },
        ],
      },
      {
        key: 'admin',
        label: 'Administration',
        modules: [
          { key: 'activity-team', label: 'Assign Team', slug: 'activity-team' },
          { key: 'settings', label: 'Settings', slug: 'settings' },
        ],
      },
    ],
  },

  // 10. Seminar
  seminar: {
    groups: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        modules: [{ key: 'overview', label: 'Overview' }],
      },
      {
        key: 'program',
        label: 'Seminar Details',
        modules: [
          { key: 'sessions', label: 'Session Details', slug: 'sessions' },
          { key: 'speaker', label: 'Keynote Speaker', slug: 'speaker' },
          { key: 'resources', label: 'Reading Materials', slug: 'resources' },
        ],
      },
      {
        key: 'attendees',
        label: 'Attendees',
        modules: [
          { key: 'participants', label: 'Attendees', slug: 'participants' },
          { key: 'attendance', label: 'Attendance', slug: 'attendance' },
          { key: 'certificates', label: 'Certificates', slug: 'certificates' },
        ],
      },
      {
        key: 'admin',
        label: 'Administration',
        modules: [
          { key: 'activity-team', label: 'Assign Team', slug: 'activity-team' },
          { key: 'settings', label: 'Settings', slug: 'settings' },
        ],
      },
    ],
  },

  // 11. Ideathon
  ideathon: {
    groups: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        modules: [{ key: 'overview', label: 'Overview' }],
      },
      {
        key: 'teams',
        label: 'Teams & Ideas',
        modules: [
          { key: 'teams', label: 'Teams', slug: 'teams' },
          { key: 'projects', label: 'Idea Pitches & Decks', slug: 'projects' },
          { key: 'mentorship', label: 'Mentors & Feedback', slug: 'mentorship' },
        ],
      },
      {
        key: 'pitching',
        label: 'Pitching & Judging',
        modules: [
          { key: 'sessions', label: 'Pitch Schedule', slug: 'sessions' },
          { key: 'evaluation', label: 'Pitch Scoring', slug: 'evaluation' },
          { key: 'leaderboard', label: 'Rankings & Grants', slug: 'leaderboard' },
          { key: 'awards', label: 'Awards & Grants', slug: 'awards' },
          { key: 'certificates', label: 'Certificates', slug: 'certificates' },
        ],
      },
      {
        key: 'admin',
        label: 'Administration',
        modules: [
          { key: 'activity-team', label: 'Assign Team', slug: 'activity-team' },
          { key: 'settings', label: 'Settings', slug: 'settings' },
        ],
      },
    ],
  },
};

/** Normalize framework_key, type, or alias into canonical ACTIVITY_TYPE_MODULES_CONFIG key */
export function resolveActivityTypeKey(activityOrType: unknown): string | null {
  if (!activityOrType) return null;

  let raw = '';
  if (typeof activityOrType === 'string') {
    raw = activityOrType;
  } else if (typeof activityOrType === 'object') {
    const act = activityOrType as Record<string, unknown>;
    raw = String(
      act.framework_key ??
        act.type ??
        act.category ??
        act.activity_type ??
        act.template_code ??
        act.framework_code ??
        '',
    );
  }

  const normalized = raw.trim().toLowerCase().replace(/[-\s]+/g, '_');
  if (!normalized) return null;

  // Direct key hit
  if (ACTIVITY_TYPE_MODULES_CONFIG[normalized]) {
    return normalized;
  }

  // Alias mappings
  const ALIASES: Record<string, string> = {
    parliamentary: 'parliamentary',
    parliament: 'parliamentary',
    youth_parliament: 'parliamentary',
    mock_parliament: 'parliamentary',
    state_legislative: 'state_legislative',
    state_legislative_assembly: 'state_legislative',
    legislative_assembly: 'state_legislative',
    mock_legislative_assembly: 'state_legislative',
    masterclass: 'seminar',
    coding_competition: 'coding_contest',
    code_contest: 'coding_contest',
    research_symposium: 'paper_presentation',
    case_competition: 'ideathon',
    innovation: 'hackathon',
  };

  if (ALIASES[normalized] && ACTIVITY_TYPE_MODULES_CONFIG[ALIASES[normalized]]) {
    return ALIASES[normalized];
  }

  // Substring matching
  if (normalized.includes('parliament')) return 'parliamentary';
  if (normalized.includes('legislative')) return 'state_legislative';
  if (normalized.includes('mun') || normalized.includes('united_nation')) return 'mun';
  if (normalized.includes('hack')) return 'hackathon';
  if (normalized.includes('conf')) return 'conference';
  if (normalized.includes('work')) return 'workshop';
  if (normalized.includes('boot')) return 'bootcamp';
  if (normalized.includes('semi')) return 'seminar';
  if (normalized.includes('quiz')) return 'quiz';
  if (normalized.includes('code') || normalized.includes('coding')) return 'coding_contest';
  if (normalized.includes('idea')) return 'ideathon';
  if (normalized.includes('paper')) return 'paper_presentation';
  if (normalized.includes('debat')) return 'debate';

  return null;
}

export function activityNavGroupsForModules(
  modules: string[],
  catalog?: ActivityModuleCatalogEntry[],
  activityOrType?: string | Record<string, unknown> | null,
): ActivityNavGroup[] {
  // If modules array is empty and no activity provided, still loading or unavailable
  if (!modules.length && !activityOrType) return [];

  const typeKey = resolveActivityTypeKey(activityOrType);
  const typeConfig = typeKey ? ACTIVITY_TYPE_MODULES_CONFIG[typeKey] : null;

  if (typeConfig) {
    const catalogByKey = new Map((catalog ?? []).map((entry) => [entry.key, entry]));
    const groups: ActivityNavGroup[] = [];

    for (const groupConfig of typeConfig.groups) {
      const items: ActivityNavItem[] = [];

      for (const modConfig of groupConfig.modules) {
        const meta = ACTIVITY_MODULE_META[modConfig.key];
        const catalogEntry = catalogByKey.get(modConfig.key);
        const typeSpecific = modConfig.key === 'parliamentary-sessions' || modConfig.key === 'legislative-sessions';
        if (modules.length > 0 && typeSpecific && !modules.includes(modConfig.key)) {
          continue;
        }

        items.push({
          key: modConfig.key,
          slug: modConfig.slug ?? catalogEntry?.slug ?? meta?.slug ?? modConfig.key,
          label: modConfig.label ?? catalogEntry?.label ?? meta?.label ?? modConfig.key,
          icon: meta?.icon ?? FileText,
          exact: modConfig.exact ?? meta?.exact,
          description: modConfig.description ?? catalogEntry?.description ?? meta?.description,
          group: groupConfig.key,
        });
      }

      if (items.length > 0) {
        groups.push({
          key: groupConfig.key,
          label: groupConfig.label,
          items,
        });
      }
    }

    const hasAwards = groups.some((group) => group.items.some((item) => item.key === 'awards'));
    if (!hasAwards) {
      const meta = ACTIVITY_MODULE_META.awards;
      groups.push({
        key: 'recognition',
        label: 'Awards',
        items: [{
          key: 'awards',
          slug: 'awards',
          label: 'Awards & Winners',
          icon: meta?.icon ?? FileText,
          description: meta?.description,
          group: 'recognition',
        }],
      });
    }

    const hasCertificates = groups.some((group) => group.items.some((item) => item.key === 'certificates'));
    if (!hasCertificates) {
      const meta = ACTIVITY_MODULE_META.certificates;
      const targetGroup = groups.find((g) => ['attendees', 'students', 'people', 'judging', 'adjudication', 'chamber', 'diplomacy', 'program', 'schedule'].includes(g.key)) || groups[1] || groups[0];
      if (targetGroup) {
        targetGroup.items.push({
          key: 'certificates',
          slug: 'certificates',
          label: 'Certificates',
          icon: meta?.icon ?? Award,
          description: meta?.description ?? 'Issue and manage certificates',
          group: targetGroup.key,
        });
      }
    }

    return groups;
  }

  // Fallback for custom / unconfigured activity types:
  // Build groups dynamically from active modules without forcing hardcoded generic items
  const items = activityNavItemsForModules(modules, catalog);
  const grouped: Record<string, ActivityNavItem[]> = {};

  for (const item of items) {
    const groupKey = item.group ?? 'program';
    if (!grouped[groupKey]) grouped[groupKey] = [];
    grouped[groupKey].push(item);
  }

  return GROUP_ORDER
    .filter((key) => (grouped[key]?.length ?? 0) > 0)
    .map((key) => ({
      key,
      label: GROUP_LABELS[key] ?? key,
      items: grouped[key],
    }));
}

export function activityNavHref(eventId: string, activityId: string, slug: string): string {
  if (!isValidEventWorkspaceId(eventId) || !isValidEventWorkspaceId(activityId)) {
    if (isValidEventWorkspaceId(eventId)) {
      return `/dashboard/events/${eventId}/activities`;
    }
    return '/dashboard/events';
  }
  const cleanSlug = slug.replace(/^\/+/, '');
  return `/dashboard/events/${eventId}/activities/${activityId}/${cleanSlug}`;
}
